import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Layers,
  ScanLine,
  Target,
  Brain,
  Database,
  Settings,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Play,
  HeartPulse,
} from 'lucide-react';
import {
  MONAITaskType,
  MONAIBundle,
  MONAIDataset,
  MONAITrainingConfig,
} from '@/mock/gpu-instance';
import MONAIBundleSelector from './MONAIBundleSelector';
import MONAIDatasetManager from './MONAIDatasetManager';
import MONAIConfigBuilder from './MONAIConfigBuilder';

interface MONAIExperimentCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bundles: MONAIBundle[];
  datasets: MONAIDataset[];
  onCreateExperiment: (experiment: {
    name: string;
    task: MONAITaskType;
    bundle: MONAIBundle;
    dataset: MONAIDataset;
    config: MONAITrainingConfig;
  }) => void;
}

const STEPS = [
  { id: 'task', title: 'Select Task', icon: Target },
  { id: 'bundle', title: 'Choose Bundle', icon: Brain },
  { id: 'dataset', title: 'Select Dataset', icon: Database },
  { id: 'config', title: 'Configure Training', icon: Settings },
  { id: 'review', title: 'Review & Create', icon: CheckCircle2 },
];

const DEFAULT_CONFIG: MONAITrainingConfig = {
  epochs: 100,
  batchSize: 4,
  learningRate: 1e-4,
  optimizer: 'adamw',
  scheduler: 'cosine',
  augmentations: ['RandFlipd', 'RandRotate90d', 'RandScaleIntensityd'],
  mixedPrecision: true,
  cachingStrategy: 'persistent',
  numWorkers: 4,
};

const MONAIExperimentCreator = ({
  open,
  onOpenChange,
  bundles,
  datasets,
  onCreateExperiment,
}: MONAIExperimentCreatorProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTask, setSelectedTask] = useState<MONAITaskType | null>(null);
  const [selectedBundle, setSelectedBundle] = useState<MONAIBundle | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<MONAIDataset | null>(null);
  const [config, setConfig] = useState<MONAITrainingConfig>(DEFAULT_CONFIG);
  const [experimentName, setExperimentName] = useState('');

  const handleReset = () => {
    setCurrentStep(0);
    setSelectedTask(null);
    setSelectedBundle(null);
    setSelectedDataset(null);
    setConfig(DEFAULT_CONFIG);
    setExperimentName('');
  };

  const handleClose = () => {
    onOpenChange(false);
    handleReset();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedTask !== null;
      case 1:
        return selectedBundle !== null;
      case 2:
        return selectedDataset !== null;
      case 3:
        return true;
      case 4:
        return experimentName.trim().length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed()) {
      setCurrentStep(currentStep + 1);
      if (currentStep === 3 && !experimentName && selectedBundle && selectedDataset) {
        setExperimentName(
          `${selectedBundle.name}_${selectedDataset.name.toLowerCase().replace(/\s+/g, '_')}`
        );
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreate = () => {
    if (selectedTask && selectedBundle && selectedDataset && experimentName) {
      onCreateExperiment({
        name: experimentName,
        task: selectedTask,
        bundle: selectedBundle,
        dataset: selectedDataset,
        config,
      });
      handleClose();
    }
  };

  const filteredBundles = selectedTask
    ? bundles.filter((b) => b.task === selectedTask)
    : bundles;

  const filteredDatasets = selectedTask
    ? datasets.filter((d) => d.task === selectedTask)
    : datasets;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeartPulse className="h-5 w-5 text-red-500" />
            Create MONAI Experiment
          </DialogTitle>
          <DialogDescription>
            Set up a new medical AI training experiment with MONAI 1.5.1
          </DialogDescription>
        </DialogHeader>

        {/* Steps Progress */}
        <div className="flex items-center justify-between py-4 border-b">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <div
                key={step.id}
                className={`flex items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div
                  className={`flex items-center gap-2 ${
                    isActive
                      ? 'text-primary'
                      : isCompleted
                      ? 'text-green-600'
                      : 'text-muted-foreground'
                  }`}
                >
                  <div
                    className={`p-2 rounded-full ${
                      isActive
                        ? 'bg-primary/10'
                        : isCompleted
                        ? 'bg-green-500/10'
                        : 'bg-muted'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <StepIcon className="h-4 w-4" />
                    )}
                  </div>
                  <span className="text-sm font-medium hidden md:block">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-500' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="py-4 min-h-[400px]">
          {/* Step 1: Select Task */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">What medical AI task do you want to train?</div>
              <div className="grid grid-cols-2 gap-4">
                <Card
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    selectedTask === 'segmentation'
                      ? 'border-primary ring-1 ring-primary'
                      : ''
                  }`}
                  onClick={() => setSelectedTask('segmentation')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-full bg-purple-500/10 w-fit mx-auto mb-4">
                      <Layers className="h-8 w-8 text-purple-500" />
                    </div>
                    <div className="font-medium text-lg mb-2">Segmentation</div>
                    <div className="text-sm text-muted-foreground">
                      Organ, tumor, and structure segmentation
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-1">
                      <Badge variant="secondary" className="text-xs">UNet</Badge>
                      <Badge variant="secondary" className="text-xs">UNETR</Badge>
                      <Badge variant="secondary" className="text-xs">SwinUNETR</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    selectedTask === 'classification'
                      ? 'border-primary ring-1 ring-primary'
                      : ''
                  }`}
                  onClick={() => setSelectedTask('classification')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-full bg-blue-500/10 w-fit mx-auto mb-4">
                      <ScanLine className="h-8 w-8 text-blue-500" />
                    </div>
                    <div className="font-medium text-lg mb-2">Classification</div>
                    <div className="text-sm text-muted-foreground">
                      Disease classification and severity grading
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-1">
                      <Badge variant="secondary" className="text-xs">DenseNet</Badge>
                      <Badge variant="secondary" className="text-xs">EfficientNet</Badge>
                      <Badge variant="secondary" className="text-xs">ResNet</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    selectedTask === 'detection'
                      ? 'border-primary ring-1 ring-primary'
                      : ''
                  }`}
                  onClick={() => setSelectedTask('detection')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-full bg-green-500/10 w-fit mx-auto mb-4">
                      <Target className="h-8 w-8 text-green-500" />
                    </div>
                    <div className="font-medium text-lg mb-2">Detection</div>
                    <div className="text-sm text-muted-foreground">
                      Nodule detection and lesion localization
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-1">
                      <Badge variant="secondary" className="text-xs">RetinaNet</Badge>
                      <Badge variant="secondary" className="text-xs">YOLO</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    selectedTask === 'registration'
                      ? 'border-primary ring-1 ring-primary'
                      : ''
                  }`}
                  onClick={() => setSelectedTask('registration')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-full bg-orange-500/10 w-fit mx-auto mb-4">
                      <Brain className="h-8 w-8 text-orange-500" />
                    </div>
                    <div className="font-medium text-lg mb-2">Registration</div>
                    <div className="text-sm text-muted-foreground">
                      Image alignment and atlas mapping
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-1">
                      <Badge variant="secondary" className="text-xs">VoxelMorph</Badge>
                      <Badge variant="secondary" className="text-xs">DeepAtlas</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 2: Choose Bundle */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Choose a MONAI Bundle</div>
              <MONAIBundleSelector
                bundles={filteredBundles}
                selectedBundleId={selectedBundle?.id}
                onSelectBundle={setSelectedBundle}
                filterTask={selectedTask || undefined}
              />
            </div>
          )}

          {/* Step 3: Select Dataset */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Select a Dataset</div>
              <MONAIDatasetManager
                datasets={filteredDatasets}
                selectedDatasetId={selectedDataset?.id}
                onSelectDataset={setSelectedDataset}
                filterTask={selectedTask || undefined}
              />
            </div>
          )}

          {/* Step 4: Configure Training */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Configure Training</div>
              <MONAIConfigBuilder
                config={config}
                onChange={setConfig}
                selectedBundle={selectedBundle || undefined}
              />
            </div>
          )}

          {/* Step 5: Review & Create */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="text-lg font-medium">Review & Create</div>

              <Card>
                <CardContent className="pt-4">
                  <Label>Experiment Name</Label>
                  <Input
                    value={experimentName}
                    onChange={(e) => setExperimentName(e.target.value)}
                    placeholder="my_monai_experiment"
                    className="mt-2 font-mono"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Task</div>
                      <div className="font-medium capitalize">{selectedTask}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Bundle</div>
                      <div className="font-medium">{selectedBundle?.displayName}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Dataset</div>
                      <div className="font-medium">{selectedDataset?.displayName}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Modality</div>
                      <div className="font-medium">{selectedBundle?.modality}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 pt-4 border-t">
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">Epochs</div>
                      <div className="font-mono font-medium">{config.epochs}</div>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">Batch</div>
                      <div className="font-mono font-medium">{config.batchSize}</div>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">LR</div>
                      <div className="font-mono font-medium">{config.learningRate}</div>
                    </div>
                    <div className="text-center p-2 bg-muted/50 rounded">
                      <div className="text-xs text-muted-foreground">Optimizer</div>
                      <div className="font-mono font-medium capitalize">{config.optimizer}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {config.augmentations.length > 0 && (
                      <Badge variant="outline">
                        {config.augmentations.length} augmentations
                      </Badge>
                    )}
                    {config.mixedPrecision && (
                      <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                        Mixed Precision
                      </Badge>
                    )}
                    {config.cachingStrategy !== 'none' && (
                      <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                        {config.cachingStrategy} cache
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={handleNext} disabled={!canProceed()}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleCreate}
                disabled={!canProceed()}
                className="bg-red-600 hover:bg-red-700"
              >
                <Play className="h-4 w-4 mr-1" />
                Create Experiment
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MONAIExperimentCreator;
